import type { CardProps } from '@/scopes/prices/components/TransportSelector/Card'
import { airlineCodesMap } from '@sunwebgroup/uds-ui/airline-icon'
import { space, SpaceBetween, tok, tokPx } from '@sunwebgroup/ui-components'
import { AirlineIcon, Button, Icon, Tag, Typography } from '@sunwebgroup/ui-components/uds'

import { identity } from 'ramda'
import { css, styled } from 'styled-components'
import { useIntl } from '@/intl'
import { LuggageType } from '@/scopes/prices/domain/transports/types'
import { UndefinedTime } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorResponseMapper'

const Remark = styled(Typography)`
  color: ${tok('primitives_color_neutral_500')};
  ${Typography.modVariant('body-xs-regular-tight')};
`

const InfoItem = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  margin-right: ${space('primitives_space_6')};
`

const AirlineWrapper = styled(SpaceBetween)`
  display: inline-flex;
  align-items: center;
  margin-right: ${space('primitives_space_6')};
`

const StyledAirlineIcon = styled(AirlineIcon)`
  height: 20px;
  width: 20px;
`

const InfoBlock = styled.div`
  flex-wrap: wrap;

  ${Icon} {
    margin-right: ${space('primitives_space_4')};
  }
`

const FlightContentMain = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`

const FlightContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${space('primitives_space_12')};
`

const FeaturesWrapper = styled.div`
    display: flex;
    align-items: center;
    padding-top: ${space('primitives_space_12')};
    width: 100%;
`

const ListItemRoot = styled.div<{ isSelected?: boolean }>`
  background-color: ${tok('semantics_views_background_color')};
  border: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  cursor: pointer;
  overflow: hidden;
  margin-bottom: ${space('primitives_space_8')};
  &:first-child {
    margin-top: 0 !important;
  }
  &:nth-child(2) {
    margin-top: 0 !important;
  }
  ${({ isSelected }) => css`
    border: ${tokPx('semantics_dividers_tickness')} solid ${isSelected ? tok('primitives_color_selected_600') : tok('semantics_dividers_color')};
  `}
`

const PriceWrapper = styled.div<{ isSelected?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${space('primitives_space_12')};
    line-height: 1.75;

  ${Tag} {
    margin-left: ${space('primitives_space_12')};
  }
  ${({ isSelected }) => css`
    background: ${isSelected ? tok('primitives_color_selected_500') : tok('primitives_color_neutral_50')};
    color: ${isSelected ? tok('primitives_color_neutral_0') : tok('primitives_color_neutral_800')};
  `}
`

const FlightContentHeading = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const FlightDepartureDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_2')};
  color: ${tok('semantics_content_secondary_color')};
`

const FlightArrivalDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_2')};
  color: ${tok('semantics_content_secondary_color')};
  margin-bottom: ${space('primitives_space_2')};
`

const FlightDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${space('primitives_space_2')};
`

const FlightDuration = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const NoFlightDurationText = styled(Typography)`
  min-width: ${space('primitives_space_44')};
`

const FlightOutboundInbound = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const FlightDurationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};
  width: 100%;
  margin: 0 ${space('primitives_space_8')};
`

const Track = styled.div`
  height: ${tokPx('primitives_space_2')};
  background: ${tok('primitives_color_neutral_100')};
  flex: 1;
`

const ContainerBetweenTracks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const TextBetweenTracks = styled(Typography)`
  white-space: nowrap;
  color: ${tok('semantics_content_secondary_color')};
`

const FlightDurationValue = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

const SelectedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_2')};
`

export function FlightCardNew({
  id,
  arrivalDate,
  departureDate,
  route,
  airline,
  luggage,
  flightType,
  averagePrice,
  isSelected,
  remark,
  setSelected = identity,
  noActiveState,
  flightDuration,
  stopovers,
}: CardProps) {
  const intl = useIntl()

  function formatFlightDurationToHours(minutes?: number | null): string {
    if (minutes == null)
      return ''

    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60

    const hoursStr = intl.formatNumber(hours, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' })
    const minsStr = intl.formatNumber(mins, { style: 'unit', unit: 'minute', unitDisplay: 'narrow', minimumIntegerDigits: 2 })

    if (hours === 0) {
      return minsStr
    }

    if (mins === 0) {
      return hoursStr
    }

    return `${hoursStr} ${minsStr}`
  }

  function formatDate(date: Date, intl: ReturnType<typeof useIntl>) {
    const formatted = intl.formatDate(date, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    return formatted.charAt(0).toUpperCase() + formatted.slice(1)
  }

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'

  return (
    <ListItemRoot isSelected={isSelected && !noActiveState} onClick={() => !isSelected && !noActiveState ? setSelected(id) : null}>
      <FlightContent>
        <FlightContentMain>
          <FlightContentHeading>
            <FlightDepartureDate>
              <Icon glyph="AirplaneTakingOff" size={16} />
              <Typography variant="body-s-regular-tight">
                {formatDate(new Date(departureDate.rawDate), intl)}
              </Typography>
            </FlightDepartureDate>
            <FlightArrivalDate>
              <Typography variant="body-s-regular-tight">
                {formatDate(new Date(arrivalDate.rawDate), intl)}
              </Typography>
              <Icon glyph="AirplaneReturn" size={16} />
            </FlightArrivalDate>
          </FlightContentHeading>
          <FlightDetails>
            <FlightDuration>
              {departureDate.time === UndefinedTime
                ? (<NoFlightDurationText variant="display-g-regular">{departureDate.time}</NoFlightDurationText>)
                : (<Typography variant="display-g-regular">{departureDate.time}</Typography>)}
              <FlightDurationWrapper>
                <Track />
                <ContainerBetweenTracks>
                  {Boolean(flightDuration) && <FlightDurationValue variant="body-s-regular-flat">{formatFlightDurationToHours(flightDuration)}</FlightDurationValue>}
                  <TextBetweenTracks variant="body-xs-regular-flat">
                    {stopovers && stopovers.length ? stopovers.join(', ') : intl.formatMessage({ id: 'prices_transport_selector_direct' })}
                  </TextBetweenTracks>
                </ContainerBetweenTracks>
                <Track />
              </FlightDurationWrapper>
              {arrivalDate.time === UndefinedTime
                ? (<NoFlightDurationText variant="display-g-regular">{arrivalDate.time}</NoFlightDurationText>)
                : (<Typography variant="display-g-regular">{arrivalDate.time}</Typography>)}
            </FlightDuration>
            <FlightOutboundInbound>
              <Typography variant="body-s-regular-tight">{route.from.name}</Typography>
              <Typography variant="body-s-regular-tight">{route.to.name}</Typography>
            </FlightOutboundInbound>
          </FlightDetails>
          <FeaturesWrapper>
            <InfoBlock>
              {airline && (
                <AirlineWrapper direction="x" spacing={space('primitives_space_4')}>
                  <StyledAirlineIcon glyph={airlineCodesMap[airline.id]} size="md" />
                  <Typography variant="body-s-regular-tight">{airline.name}</Typography>
                </AirlineWrapper>
              )}
              {luggage && flightType && luggage.map(luggageItem => (
                <InfoItem key={luggageItem.type}>
                  <Icon glyph={luggageItem.type === LuggageType.Hold ? 'Suitcase' : 'BackpackWithCheckMark'} size={18} />
                  <Typography variant="body-s-regular-tight">
                    {luggageItem.type === LuggageType.Hold && `${luggageItem.weight} kg`}
                    {luggageItem.type === LuggageType.Hand
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </InfoItem>
              ))}
              <InfoItem>
                <Icon glyph={`${flightType === 'standard' ? 'AirplaneToTop' : 'AirplaneWithArrowsCirclePath'}`} size={18} />
                <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </InfoItem>
            </InfoBlock>
            {remark && <Remark>{remark}</Remark>}
          </FeaturesWrapper>
        </FlightContentMain>
      </FlightContent>
      <PriceWrapper isSelected={isSelected && !noActiveState}>
        <div>
          <Typography variant="body-m-bold-tight">{intl.formatCurrency(averagePrice, { showSign: true })}</Typography>
          <Typography variant="body-m-bold-tight">
            {' '}
            {intl.formatMessage({ id: 'prices_transport_selector_price-per-person_abbreviation' })}
          </Typography>
          {averagePrice === 0 && (
            <Tag variant="success-tinted">{intl.formatMessage({ id: 'prices_transport_selector_flight-selector_cheapest' })}</Tag>
          )}
        </div>
        { !isSelected && (
          <Button size="S" variant="secondary">
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-select',
            })}
          </Button>
        )}
        { isSelected && (
          <SelectedLabel>
            <Icon glyph="CheckMarkSmall" />
            <Typography variant="body-m-regular-tight">{intl.formatMessage({ id: 'prices_transport_selector_flight-selector_selected' })}</Typography>
          </SelectedLabel>
        )}
      </PriceWrapper>
    </ListItemRoot>
  )
}
